import { AlertTriangle, RefreshCw } from 'lucide-react';

import { Button } from '@/components/ui/Button';
import { env } from '@/lib/env';
import { useBackendHealth } from '@/services/system';

export function BackendStatusBanner() {
  const healthQuery = useBackendHealth();

  // Only show after all retries have been exhausted (failureCount >= 3), not on
  // the first failed attempt — avoids false positives during page load or brief
  // network hiccups.
  const isError = healthQuery.isError && (healthQuery.failureCount ?? 0) >= 3;
  const isDegraded = healthQuery.data?.status && healthQuery.data.status !== 'healthy';

  if (!isError && !isDegraded) {
    return null;
  }

  const message = isError
    ? `Unable to reach Advoltix API at ${env.apiBaseUrl}.`
    : 'Advoltix API is responding but reports degraded status.';

  const secondary = isError
    ? 'Check VITE_API_BASE_URL in your environment configuration or ensure the backend is running.'
    : 'Some dependent services are reporting issues. Investigate health checks for details.';

  return (
    <div className="sticky top-0 z-[100] w-full border-b border-rose-500/40 bg-rose-500/15 backdrop-blur">
      <div className="mx-auto flex w-full max-w-7xl items-center justify-between gap-4 px-4 py-3 text-sm text-rose-100">
        <div className="flex items-start gap-3">
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-none text-rose-200" />
          <div>
            <p className="font-semibold text-rose-100">{message}</p>
            <p className="text-xs text-rose-200/80">{secondary}</p>
          </div>
        </div>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="text-rose-100 hover:bg-rose-500/20"
          onClick={() => healthQuery.refetch()}
          disabled={healthQuery.isRefetching}
        >
          <RefreshCw className="mr-2 h-3.5 w-3.5" />
          {healthQuery.isRefetching ? 'Checking…' : 'Retry'}
        </Button>
      </div>
    </div>
  );
}
